'use client';

import styles from './pinholder.module.sass';
import classnames from 'classnames';
import React, { useEffect } from 'react';

type AllowedInputTypes = 'password' | 'text' | 'number' | 'tel';

interface OTPCredentialRequestOptions extends CredentialRequestOptions {
    otp?: {
        transport: string[];
    };
}

interface OTPCredential {
    code: string;
}

type InputProps = Required<
    Pick<
        React.InputHTMLAttributes<HTMLInputElement>,
        | 'value'
        | 'onChange'
        | 'onFocus'
        | 'onBlur'
        | 'onKeyDown'
        | 'onPaste'
        | 'aria-label'
        | 'maxLength'
        | 'autoComplete'
        | 'style'
        | 'inputMode'
        | 'onInput'
        | 'name'
        | 'id'
        | 'readOnly'
    > & {
        ref: React.RefCallback<HTMLInputElement>;
        placeholder: string | undefined;
        className: string | undefined;
        type: AllowedInputTypes;
        ['data-test-id']: string;
    }
>;

interface PinHolderInputProps {
    /** Value of the OTP input */
    value?: string;
    /** Number of OTP inputs to be rendered */
    numInputs?: number;
    /** Callback to be called when the OTP value changes */
    onChange: (otp: string) => void;
    /** Callback to be called when pasting content into the component */
    onPaste?: (event: React.ClipboardEvent<HTMLElement>) => void;
    /** Function to render the input */
    renderInput: (inputProps: InputProps, index: number) => React.ReactNode;
    /** Whether the first input should be auto focused */
    shouldAutoFocus?: boolean;
    /** Placeholder for the inputs */
    placeholder?: string;
    /** Function to render the separator */
    renderSeparator?: ((index: number) => React.ReactNode) | React.ReactNode;
    /** Style for the container */
    containerStyle?: React.CSSProperties | string;
    /** Style for the input */
    inputStyle?: React.CSSProperties | string;
    /** The type that will be passed to the input being rendered */
    inputType?: AllowedInputTypes;
    /** Do not apply the default styles to the inputs, will be removed in future versions */
    skipDefaultStyles?: boolean; // TODO: Remove in next major release
}

const isStyleObject = (obj: any): obj is React.CSSProperties => typeof obj === 'object' && obj !== null;

const PinHolder = ({
    value = '',
    numInputs = 4,
    onChange,
    onPaste,
    renderInput,
    shouldAutoFocus = false,
    inputType = 'text',
    renderSeparator,
    placeholder,
    containerStyle,
    inputStyle,
    skipDefaultStyles = false,
}: PinHolderInputProps) => {
    const [activeInput, setActiveInput] = React.useState(0);
    const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
    // Unlock typing after mount / first genuine user focus. Blocks saved-password autofill.
    const [isAutofillBlocked, setIsAutofillBlocked] = React.useState(true);
    // useId() yields ":r1:"; the colons are legal in an id but break `querySelector('#' + id)`
    // in password managers and analytics scripts that build selectors from our ids.
    const fieldIdPrefix = React.useId().replace(/[^a-zA-Z0-9_-]/g, '');

    const getOTPValue = () => (value ? value.toString().split('') : []);

    const isInputNum = inputType === 'number' || inputType === 'tel';

    const clearDomValuesIfEmpty = React.useCallback(() => {
        if (value) {
            return;
        }
        inputRefs.current.forEach((input) => {
            if (input?.value) {
                input.value = '';
                input.removeAttribute('value');
            }
        });
    }, [value]);

    React.useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, numInputs);
    }, [numInputs]);

    React.useEffect(() => {
        // Keep fields read-only long enough for Chrome/password managers to finish autofill attempts.
        const unlockTimer = window.setTimeout(() => {
            setIsAutofillBlocked(false);
            clearDomValuesIfEmpty();
        }, 1000);

        return () => window.clearTimeout(unlockTimer);
    }, [clearDomValuesIfEmpty]);

    React.useEffect(() => {
        if (shouldAutoFocus && !isAutofillBlocked) {
            inputRefs.current[0]?.focus();
        }
    }, [shouldAutoFocus, isAutofillBlocked]);

    // Keep clearing autofilled DOM values while React value is still empty.
    React.useEffect(() => {
        if (value) {
            return;
        }

        clearDomValuesIfEmpty();
        const intervalId = window.setInterval(clearDomValuesIfEmpty, 100);
        const stopTimer = window.setTimeout(() => window.clearInterval(intervalId), 1500);

        return () => {
            window.clearInterval(intervalId);
            window.clearTimeout(stopTimer);
        };
    }, [value, clearDomValuesIfEmpty]);

    // Auto Fill SMS OTP using navigator WebOTP API
    useEffect(() => {
        if ('OTPCredential' in window) {
            const ac = new AbortController();
            const signal = ac.signal;

            navigator.credentials
                .get({
                    otp: { transport: ['sms'] },
                    signal,
                } as OTPCredentialRequestOptions)
                .then((otpCredential) => {
                    const otp = (otpCredential as unknown as OTPCredential)?.code;
                    if (otp) {
                        onChange(otp);
                    }
                })
                .catch((err) => {
                    // AbortError is expected when PinHolder unmounts (e.g. profile switch / navigation).
                    if (err?.name === 'AbortError') {
                        return;
                    }
                    console.warn('Error fetching navigator WebOTP: ', err);
                });

            // Cleanup on unmount component
            return () => ac.abort();
        }
    }, []);

    const getPlaceholderValue = () => {
        if (typeof placeholder === 'string') {
            if (placeholder.length === numInputs) {
                return placeholder;
            }

            if (placeholder.length > 0) {
                console.error('Length of the placeholder should be equal to the number of inputs.');
            }
        }
        return undefined;
    };

    const isInputValueValid = (value: string) => {
        const isTypeValid = isInputNum ? !isNaN(Number(value)) : typeof value === 'string';
        return isTypeValid && value.trim().length === 1;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value: inputValue } = event.target;

        // Password managers often dump a full saved password into one box.
        if (inputValue.length > 1) {
            event.target.value = '';
            // Allow genuine OTP paste of exact length digits only.
            if (isInputNum && /^\d+$/.test(inputValue) && inputValue.length === numInputs) {
                handleOTPChange(inputValue.split(''));
                focusInput(numInputs - 1);
            }
            return;
        }

        if (isInputValueValid(inputValue)) {
            changeCodeAtFocus(inputValue);
            focusInput(activeInput + 1);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { nativeEvent } = event;
        if (!isInputValueValid(event.target.value)) {
            // @ts-expect-error - This was added previosly to handle and edge case
            // for dealing with keyCode "229 Unidentified" on Android. Check if this is
            // still needed.
            if (nativeEvent.data === null && nativeEvent.inputType === 'deleteContentBackward') {
                event.preventDefault();
                changeCodeAtFocus('');
                focusInput(activeInput - 1);
            }
            // Clear the input if it's not valid value because firefox allows
            // pasting non-numeric characters in a number type input
            event.target.value = '';
        }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => (index: number) => {
        setIsAutofillBlocked(false);
        setActiveInput(index);
        event.target.select();
    };

    const handleBlur = () => {
        setActiveInput(activeInput - 1);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const otp = getOTPValue();
        if ([event.code, event.key].includes('Backspace')) {
            event.preventDefault();
            changeCodeAtFocus('');
            focusInput(activeInput - 1);
        } else if (event.code === 'Delete') {
            event.preventDefault();
            changeCodeAtFocus('');
        } else if (event.code === 'ArrowLeft') {
            event.preventDefault();
            focusInput(activeInput - 1);
        } else if (event.code === 'ArrowRight') {
            event.preventDefault();
            focusInput(activeInput + 1);
        }
        // React does not trigger onChange when the same value is entered
        // again. So we need to focus the next input manually in this case.
        else if (event.key === otp[activeInput]) {
            event.preventDefault();
            focusInput(activeInput + 1);
        } else if (
            event.code === 'Spacebar' ||
            event.code === 'Space' ||
            event.code === 'ArrowUp' ||
            event.code === 'ArrowDown'
        ) {
            event.preventDefault();
        }
    };

    const focusInput = (index: number) => {
        const activeInput = Math.max(Math.min(numInputs - 1, index), 0);

        if (inputRefs.current[activeInput]) {
            inputRefs.current[activeInput]?.focus();
            inputRefs.current[activeInput]?.select();
            setActiveInput(activeInput);
        }
    };

    const changeCodeAtFocus = (value: string) => {
        const otp = getOTPValue();
        otp[activeInput] = value[0];
        handleOTPChange(otp);
    };

    const handleOTPChange = (otp: Array<string>) => {
        const otpValue = otp.join('');
        onChange(otpValue);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();

        const otp = getOTPValue();
        let nextActiveInput = activeInput;

        // Get pastedData in an array of max size (num of inputs - current position)
        const pastedData = event.clipboardData
            .getData('text/plain')
            .slice(0, numInputs - activeInput)
            .split('');

        // Prevent pasting if the clipboard data contains non-numeric values for number inputs
        if (isInputNum && pastedData.some((value) => isNaN(Number(value)))) {
            return;
        }

        // Paste data from focused input onwards
        for (let pos = 0; pos < numInputs; ++pos) {
            if (pos >= activeInput && pastedData.length > 0) {
                otp[pos] = pastedData.shift() ?? '';
                nextActiveInput++;
            }
        }

        focusInput(nextActiveInput);
        handleOTPChange(otp);
    };

    return (
        <form
            data-test-id={'pin-holder'}
            className={typeof containerStyle === 'string' ? containerStyle : classnames(styles['pin-holder'])}
            dir="ltr"
            style={{
                display: 'flex',
                alignItems: 'center',
                ...(isStyleObject(containerStyle) ? containerStyle : {}),
            }}
            onPaste={onPaste ? (event) => onPaste(event) : undefined}
            autoComplete="off"
            onSubmit={(event) => event.preventDefault()}
        >
            {/*
              Decoy fields absorb Chrome/password-manager saved credentials so they
              do not land in the real single-digit PIN boxes. They must stay labelled:
              password managers resolve a label for every credential field they find and
              several of them dereference the result without a null check.
            */}
            <div
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    opacity: 0,
                    pointerEvents: 'none',
                }}
            >
                <label htmlFor={`${fieldIdPrefix}-decoy-username`}>Username</label>
                <input
                    id={`${fieldIdPrefix}-decoy-username`}
                    type="text"
                    name="username"
                    autoComplete="username"
                    tabIndex={-1}
                    value=""
                    readOnly
                />
                <label htmlFor={`${fieldIdPrefix}-decoy-password`}>Password</label>
                <input
                    id={`${fieldIdPrefix}-decoy-password`}
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    tabIndex={-1}
                    value=""
                    readOnly
                />
            </div>
            {Array.from({ length: numInputs }, (_, index) => index).map((index) => {
                const inputId = `${fieldIdPrefix}-otp-${index}`;
                return (
                    <div key={`pin_${index}`} className={styles.input}>
                        {/*
                          Without this the input is the first element child of the wrapper, so
                          `input.previousElementSibling` is null. Injected autofill/security
                          scripts hook document focus events and read `.innerText` off that
                          sibling without a null check, which throws on every PIN keystroke.
                          `hidden` keeps it out of layout and the accessibility tree, and does
                          not disturb the `input:focus + label` underline rule.
                        */}
                        <span hidden aria-hidden="true" />
                        <React.Fragment key={index}>
                            {renderInput(
                                {
                                    value: getOTPValue()[index] ?? '',
                                    placeholder: getPlaceholderValue()?.[index] ?? '',
                                    ref: (element) => {
                                        inputRefs.current[index] = element;
                                    },
                                    onChange: handleChange,
                                    onFocus: (event) => handleFocus(event)(index),
                                    onBlur: handleBlur,
                                    onKeyDown: handleKeyDown,
                                    onPaste: handlePaste,
                                    // Tell browsers this is a one-time code, not a password field.
                                    autoComplete: 'one-time-code',
                                    maxLength: 1,
                                    'aria-label': `Please enter OTP character ${index + 1}`,
                                    style: {
                                        ...(!skipDefaultStyles ? { width: '1em', textAlign: 'center' } : {}),
                                        ...(isStyleObject(inputStyle) ? inputStyle : {}),
                                    },
                                    className: typeof inputStyle === 'string' ? inputStyle : undefined,
                                    type: inputType,
                                    inputMode: isInputNum ? 'numeric' : 'text',
                                    onInput: handleInputChange,
                                    'data-test-id': `pin-input-${index}`,
                                    name: inputId,
                                    id: inputId,
                                    readOnly: isAutofillBlocked,
                                },
                                index
                            )}
                            {index < numInputs - 1 &&
                                (typeof renderSeparator === 'function' ? renderSeparator(index) : renderSeparator)}
                        </React.Fragment>
                        <label htmlFor={inputId}></label>
                    </div>
                );
            })}
        </form>
    );
};

export type { PinHolderInputProps };

export { PinHolder };
